import { S as Swiper } from './classes-to-selector-6ijYtnfY.js';
import { N as Navigation } from './navigation-D3ssu5k4.js';
import { P as Pagination } from './pagination-6ibxO3Ub.js';
import { S as Scrollbar } from './scrollbar-CigCyCA3.js';
import { A as A11y } from './a11y-DZ2R8bcp.js';
import { A as Autoplay } from './autoplay-C12qvRhh.js';

/**
 * @class SwiperSlider
 * @extends {HTMLElement}
 * @description Create a swiper slider, configurable with data-options attribute.
 * @example
 * <swiper-slider data-swiper='{ "threshold": 10, "slidesPerView": 1 }'>
 *  <div class="swiper" data-swiper>
 *    <div class="swiper-wrapper">
 *      <div class="swiper-slide">Slide 1</div>
 *      <div class="swiper-slide">Slide 2</div>
 *      <div class="swiper-slide">Slide 3</div>
 *    </div>
 *  </div>
 * </swiper-slider>
 */
class SwiperSlider extends HTMLElement {
  connectedCallback() {
    this.swiper = this.querySelector('[data-swiper]');
    if (!this.swiper) return;

    // Default to improve user experience
    this.swiperOptions = {
      modules: [A11y, Navigation, Pagination, Scrollbar, Autoplay],
      threshold: 10,
    };

    const options = JSON.parse(this.swiper.dataset.options || '{}');
    this.swiperOptions = {
      ...this.swiperOptions,
      ...options,
    };

    // Call swiper with selected swiper element and options
    this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);

    // Handle custom aria-label functionality
    // This is used for Eye-Able fixes for main-product-tabs
    if (this.swiperOptions.customAriaLabel) {
      this.setCustomAriaLabels();
    }

    // Listen to extra events when in Shopify editor
    if (!Shopify.designMode) return;
    console.log('> Shopify.designMode: ', Shopify.designMode);

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // When on block select go to the slide in front-end
    window.addEventListener(
      'shopify:block:select',
      (event) => {
        this.handleBlockSelect(event);
      },
      { signal }
    );

    // When on block deselect go to the slide in front-end
    window.addEventListener(
      'shopify:block:deselect',
      (event) => {
        this.handleBlockSelect(event);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.swiperInstance?.destroy(true, true);
    this.abortController?.abort();
  }

  /**
   * @method setCustomAriaLabels
   * @description Sets custom aria-label attributes on slides based on their aria-labelledby values
   * @returns {void}
   */
  setCustomAriaLabels() {
    const slides = this.swiperInstance.slides;
    slides.forEach((slide) => {
      const ariaLabelledBy = slide.getAttribute('aria-labelledby');
      if (ariaLabelledBy) {
        slide.setAttribute('aria-label', ariaLabelledBy);
      }
    });
  }

  /**
   * @method handleBlockSelect
   * @description Handles the theme editor block change/edit event
   * @param {Object} event
   */
  handleBlockSelect(event) {
    this.swiperInstance.update();
    if (!('swiperSlideIndex' in event.target.dataset)) return;
    this.swiperInstance.slideTo(parseInt(event.target.dataset.swiperSlideIndex), 1000);
  }
}

customElements.define('swiper-slider', SwiperSlider);
